import React, { useState, useEffect, useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useHookstate } from "@hookstate/core";
import globalAuthStateStore from "../../../../../stores/globalAuthStateStore";
import { VirtualLibrarianService } from "../../../../../services/VirtualLibrarianService";
import { Tabs, Tab, Box } from "@mui/material";
import {
  getContentType,
  getVideoTitle,
  getDocumentTitle,
} from "../utilities/chatAnswerUtils";
import DOMPurify from "dompurify";
import VirtualLibrarianResultContentList from "./citationResults";
import spinnerIcon from "../../../../../content/icons/ee_spinner.gif";
import echoImage from "../../../../../content/icons/echoImage.png";
import "./chatInterface.scss";
import virtualLibrarianSettingsStore from "../../../../../stores/virtualLibrarianSettingsStore";
import TabPanel from "./tabPanel";
import { processCitationsInAnswer } from "../../../../../utilities/VirtualLibrarianUtils";
import VideosIcon from "./VideosIcon";
import FileIcon from "./FileIcon";

const VirtualLibrarianChat = ({ mode }) => {
  const [sessionId, setSessionId] = useState(() => {
    const savedSessionId = sessionStorage.getItem("virtualLibrarianSessionId");
    return savedSessionId ? savedSessionId : "";
  });
  const [chatItems, setChatItems] = useState(() => {
    const savedChatItems = sessionStorage.getItem("virtualLibrarianChatItems");
    return savedChatItems ? JSON.parse(savedChatItems) : [];
  });
  const [videoCitations, setVideoCitations] = useState(() => {
    const savedVideoCitations = sessionStorage.getItem(
      "virtualLibrarianVideoCitations"
    );
    return savedVideoCitations ? JSON.parse(savedVideoCitations) : [];
  }); // State for video citations
  const [documentCitations, setDocumentCitations] = useState(() => {
    const savedDocumentCitations = sessionStorage.getItem(
      "virtualLibrarianDocumentCitations"
    );
    return savedDocumentCitations ? JSON.parse(savedDocumentCitations) : [];
  }); // State for document citations
  const [citationCount, setCitationCount] = useState(() => {
    const savedCitationCount = sessionStorage.getItem(
      "virtualLibrarianCitationCount"
    );
    return savedCitationCount
      ? JSON.parse(savedCitationCount)
      : documentCitations?.length + videoCitations?.length;
  }); // State for document citations

  const [isThinking, setIsThinking] = useState(false); // New state for thinking indicator
  const [selectedTab, setSelectedTab] = useState(0); // State for selected tab index
  const [suggestedQuestions, setSuggestedQuestions] = useState([]); // State for suggested questions

  const settings = useHookstate(virtualLibrarianSettingsStore);
  const authStore = useHookstate(globalAuthStateStore);
  const vlService = React.useMemo(
    () => new VirtualLibrarianService(authStore),
    [authStore]
  );

  const chatItemsEndRef = useRef(null);

  const scrollToBottom = () => {
    chatItemsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatItems, isThinking]);

  const handleCitationClick = (event) => {
    const target = event.target;
    if (target.tagName === "SUP" && target.dataset.targetTab) {
      const targetTab = target.dataset.targetTab;
      if (targetTab === "video") {
        setSelectedTab(1); // Assuming AI Results is 0, Video is 1
      } else if (targetTab === "document") {
        // Adjust index based on whether videoCitations tab is present
        const documentTabIndex = videoCitations?.length > 0 ? 2 : 1;
        setSelectedTab(documentTabIndex);
      }
    }
  };

  useEffect(() => {
    loadSettings();
  }, [settings]);

  // Separate useEffect for fetching suggested questions to avoid dependency issues
  useEffect(() => {
    fetchSuggestedQuestions();
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      "virtualLibrarianChatItems",
      JSON.stringify(chatItems)
    );
    window.dispatchEvent(new Event("virtualLibrarianChatItems"));
  }, [chatItems]);

  useEffect(() => {
    sessionStorage.setItem(
      "virtualLibrarianVideoCitations",
      JSON.stringify(videoCitations)
    );
  }, [videoCitations]);

  useEffect(() => {
    sessionStorage.setItem(
      "virtualLibrarianDocumentCitations",
      JSON.stringify(documentCitations)
    );
  }, [documentCitations]);

  useEffect(() => {
    sessionStorage.setItem("virtualLibrarianSessionId", sessionId);
  }, [sessionId]);

  useEffect(() => {
    const pendingQuestion = sessionStorage.getItem(
      "virtualLibrarianVideoQuestion"
    );
    if (pendingQuestion?.trim()) {
      setSelectedTab(0);
      askQuestion(pendingQuestion);
      sessionStorage.setItem("virtualLibrarianVideoQuestion", "");
    }
  }, []);

  const fetchSuggestedQuestions = async () => {
    try {
      console.log("Fetching suggested questions...");
      // Get recent chat logs to extract questions
      const result = await vlService.getChatLogsById();
      console.log("API result:", result);

      // Check if we have valid data
      if (result && result.Success && result.Data) {
        console.log("Chat logs data:", result.Data);
        const questions = [];

        // Handle both array and single object responses
        const logsArray = Array.isArray(result.Data)
          ? result.Data
          : [result.Data];

        // Process each log entry
        for (const log of logsArray) {
          // Extract question directly from the Question field and timestamp
          if (log.Question && typeof log.Question === "string") {
            console.log("Found question:", log.Question);

            // Count document and video citations
            let documentCount = 0;
            let videoCount = 0;

            // Process citations if they exist
            if (log.Citations) {
              try {
                // Parse citations if they're in string format
                const citationsData =
                  typeof log.Citations === "string"
                    ? JSON.parse(log.Citations)
                    : log.Citations;

                if (citationsData && Array.isArray(citationsData)) {
                  // Count unique content IDs for documents and videos
                  const documentIds = new Set();
                  const videoIds = new Set();

                  citationsData.forEach((citation) => {
                    const docType = citation.type || citation.Type;
                    const contentId =
                      citation.content_id || citation.ContentId || "";

                    if (contentId) {
                      if (docType === "video_section") {
                        videoIds.add(contentId);
                      } else {
                        documentIds.add(contentId);
                      }
                    }
                  });

                  documentCount = documentIds.size;
                  videoCount = videoIds.size;
                }
              } catch (e) {
                console.error("Error parsing citations for question:", e);
              }
            }

            questions.push({
              text: log.Question,
              timestamp: log.CreatedOn || new Date().toISOString(),
              documentCount: documentCount,
              videoCount: videoCount,
            });
          }
        }

        // Sort by timestamp (most recent first)
        questions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        // No longer limiting to 3 questions, display all unique questions
        console.log("Final questions to display:", questions);

        if (questions.length > 0) {
          setSuggestedQuestions(questions);
        } else {
          console.log("No questions found in logs");
          setSuggestedQuestions([]);
        }
      } else {
        console.log("No valid data returned from API");
        setSuggestedQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching suggested questions:", error);
      setSuggestedQuestions([]);
    }
  };

  const loadSettings = async () => {
    if (!settings.IS_LOADED.get()) {
      const getSettingsResult = await vlService.getVirtualLibrarianSettings();
      if (getSettingsResult.Success) {
        const searchIndexCountSetting = getSettingsResult.Data.filter(
          (s) => s.setting_name === "SEARCH_INDEX_COUNT"
        )[0];
        if (searchIndexCountSetting) {
          settings.SEARCH_INDEX_COUNT.set({
            ...settings.SEARCH_INDEX_COUNT.get(),
            int_value: searchIndexCountSetting.int_value,
          });
        }

        const returnAllRagResultsSetting = getSettingsResult.Data.filter(
          (s) => s.setting_name === "RETURN_ALL_RAG_RESULTS"
        )[0];
        if (returnAllRagResultsSetting) {
          settings.RETURN_ALL_RAG_RESULTS.set({
            ...settings.RETURN_ALL_RAG_RESULTS.get(),
            bool_value: returnAllRagResultsSetting.bool_value,
          });
        }
        settings.IS_LOADED.set(true);
      }
    }
  };

  const parseAnswerResult = (result) => {
    const citations = [];
    const followupQuestions = [];

    // Extract any follow-up questions that might be in the answer
    let parsedAnswer = result?.Data?.answer?.replace(
      /<<([^>>]+)>>/g,
      (match, content) => {
        followupQuestions.push(content);
        return "";
      }
    );

    // trim any whitespace from the end of the answer after removing follow-up questions
    parsedAnswer = parsedAnswer?.trim();

    const parts = parsedAnswer?.split(/\[([^\]]+)\]/g);

    const fragments = parts?.map((part, index) => {
      if (index % 2 === 0) {
        return part;
      } else {
        let citationIndex;
        if (citations.indexOf(part) !== -1) {
          citationIndex = citations.indexOf(part) + 1;
        } else {
          citations.push(part);
          citationIndex = citations?.length;
        }

        const type = getContentType(part, result.Data.docs_by_id);
        const title =
          type === "video_section"
            ? getVideoTitle(part, result.Data.docs_by_id)
            : getDocumentTitle(part, result.Data.docs_by_id);

        // Determine the target tab based on the content type
        const targetTab = type === "video_section" ? "video" : "document";

        return renderToStaticMarkup(
          <a className="supContainer" title={title}>
            <sup data-docid={part} data-target-tab={targetTab}>
              {citationIndex + citationCount}
            </sup>
          </a>
        );
      }
    });

    const answerHtmlFragments = fragments?.join("");
    setCitationCount(
      (prevCitationCount) =>
        prevCitationCount +
        fragments.filter((fragment) => fragment.includes("supContainer"))
          ?.length
    );

    return {
      answerText: result.Data.answer,
      answerHtml: answerHtmlFragments,
      citations: citations.map((c, index) => {
        return { id: c, index: index + citationCount + 1 };
      }),
      followupQuestions,
      sanitizedAnswerHtml: DOMPurify.sanitize(answerHtmlFragments),
    };
  };

  const askQuestion = async (questionText) => {
    // Accept question text as argument
    setIsThinking(true); // Set thinking state to true when starting to ask a question

    const askPayload = {
      question: questionText,
      approach: "rrrv",
      overrides: {
        semantic_captions: false,
        semantic_ranker: false,
        top: settings.SEARCH_INDEX_COUNT.int_value.get() ?? 6,
        include_all_results:
          settings.RETURN_ALL_RAG_RESULTS.bool_value.get() ?? false,
      },
      chat_history: chatItems.map((item) => ({
        question: item.question,
        answer: item.answer,
      })),
      chat_session_id:
        sessionId && sessionId?.length === 36 ? sessionId : undefined,
    };

    const result = await vlService.askQuestion(askPayload);

    if (result) {
      setSessionId(result.Data.chat_session_id);
      var answer = parseAnswerResult(result);
      console.log(answer);
      // Use functional update to ensure we have the latest state
      setChatItems((prevChatItems) => [
        ...prevChatItems,
        {
          question: questionText,
          answer: answer.answerText,
          answerHtml: answer.sanitizedAnswerHtml,
        },
      ]);

      // Refresh the previous questions panel after successful ask
      fetchSuggestedQuestions();

      // Process citations to separate video and document types
      if (
        answer.citations &&
        Array.isArray(answer.citations) &&
        result.Data.docs_by_id
      ) {
        const currentVideoCitations = [];
        const currentDocumentCitations = [];

        answer.citations.forEach((citation) => {
          const doc = result.Data.docs_by_id[citation.id];
          if (doc) {
            // Check if the document exists for the guid
            if (doc.type === "video_section") {
              if (!currentVideoCitations.includes((d) => d.id === doc.id)) {
                currentVideoCitations.push({ ...doc, index: citation.index });
              }
            } else if (doc.type === "document_split") {
              if (!currentDocumentCitations.includes((d) => d.id === doc.id)) {
                currentDocumentCitations.push({
                  ...doc,
                  index: citation.index,
                });
              }
            }
          }
        });

        console.log("Filtered Video Citations:", currentVideoCitations);
        setVideoCitations((prevVideoCitations) => [
          ...prevVideoCitations,
          ...currentVideoCitations,
        ]);
        console.log("Filtered Document Citations:", currentDocumentCitations);
        setDocumentCitations((prevDocumentCitations) => [
          ...prevDocumentCitations,
          ...currentDocumentCitations,
        ]);
      } else {
        // Clear citations if the conditions aren't met (e.g., no citations or docs_by_id)
        setVideoCitations([]);
        setDocumentCitations([]);
      }
    }
    setIsThinking(false); // Set thinking state to false when done
  };

  const handleSubmitQuestion = () => {
    const question =
      sessionStorage.getItem("virtualLibrarianVideoQuestion") ?? "";

    if (question?.length > 0 && question.trim()) {
      setSelectedTab(0);
      // Clear the sessionStorage before asking the question to prevent double execution
      sessionStorage.setItem("virtualLibrarianVideoQuestion", "");
      askQuestion(question);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const showPreviousQuestionLog = async (questionText) => {
    setIsThinking(true); // Show thinking state while fetching

    try {
      // Get chat logs by user ID
      const result = await vlService.getChatLogsById();

      if (result && result.Success && result.Data) {
        // Handle both array and single object responses
        const logsArray = Array.isArray(result.Data)
          ? result.Data
          : [result.Data];

        // Find the chat log that matches the question text
        const matchingLog = logsArray.find(
          (log) => log.Question === questionText
        );

        if (matchingLog) {
          console.log("Found matching log:", matchingLog);

          // Parse citations from the response if they exist
          let answer = {
            answerText: matchingLog.Response || "",
            answerHtml: matchingLog.Response || "",
          };

          // Process citations if they exist
          if (matchingLog.Citations) {
            let citations = [];
            let documentCitationsArray = [];
            let videoCitationsArray = [];

            try {
              // Parse citations if they're in string format
              const citationsData =
                typeof matchingLog.Citations === "string"
                  ? JSON.parse(matchingLog.Citations)
                  : matchingLog.Citations;

              if (citationsData && Array.isArray(citationsData)) {
                // Get the current citation count to ensure continuity in numbering
                const currentCitationCount = citationCount;

                // Process each citation
                citationsData.forEach((citation, index) => {
                  const citationId = citation.id || citation.Id;
                  const docType = citation.type || citation.Type;

                  if (citationId) {
                    // Calculate the new index based on current citation count
                    const newIndex = currentCitationCount + index + 1;

                    // Add to citations array with correct index
                    citations.push({
                      id: citationId,
                      index: newIndex,
                    });

                    // Create citation object with all needed properties
                    const citationObj = {
                      ...citation,
                      id: citationId,
                      index: newIndex,
                      content_id:
                        citation.content_id || citation.ContentId || "",
                    };

                    // Sort between document and video citations
                    if (docType === "video_section") {
                      videoCitationsArray.push(citationObj);
                    } else {
                      documentCitationsArray.push(citationObj);
                    }
                  }
                });

                // Process answer HTML with citations
                answer.answerHtml = processCitationsInAnswer(
                  answer.answerText,
                  citations,
                  currentCitationCount
                );
                answer.sanitizedAnswerHtml = DOMPurify.sanitize(
                  answer.answerHtml
                );
              }
            } catch (e) {
              console.error("Error parsing citations:", e);
            }

            // Update citations state - append to existing arrays
            setDocumentCitations((prevDocCitations) => [
              ...prevDocCitations,
              ...documentCitationsArray,
            ]);
            setVideoCitations((prevVideoCitations) => [
              ...prevVideoCitations,
              ...videoCitationsArray,
            ]);
            setCitationCount((prevCount) => prevCount + citations.length);
          }

          // Add to chat items
          setChatItems((prevChatItems) => [
            ...prevChatItems,
            {
              question: questionText,
              answer: answer.answerText,
              answerHtml: answer.sanitizedAnswerHtml || answer.answerText,
              feedback: matchingLog.Feedback || null,
            },
          ]);

          // Make sure we're showing the AI Results tab
          setSelectedTab(0);
        } else {
          // If we couldn't find the log, fall back to asking the question again
          console.log("No matching log found, asking question instead");
          askQuestion(questionText);
        }
      } else {
        // If we couldn't get logs, fall back to asking the question again
        console.log("No valid logs data returned, asking question instead");
        askQuestion(questionText);
      }
    } catch (error) {
      console.error("Error getting chat logs:", error);
      // Fall back to asking the question
      askQuestion(questionText);
    } finally {
      setIsThinking(false);
    }
  };

  const handleClearChat = () => {
    setChatItems([]);
    setVideoCitations([]);
    setDocumentCitations([]);
    setSelectedTab(0);
    setSessionId("");
    setCitationCount(0);
    sessionStorage.setItem("virtualLibrarianVideoQuestion", "");
    sessionStorage.removeItem("virtualLibrarianChatItems");
    sessionStorage.removeItem("virtualLibrarianVideoCitations");
    sessionStorage.removeItem("virtualLibrarianDocumentCitations");

    window.dispatchEvent(new Event("virtualLibrarianChatItems"));
  };

  useEffect(() => {
    const prevQuestion =
      sessionStorage.getItem("previousQuestionSubmitted") ?? "";
    if (prevQuestion?.length > 0) {
      showPreviousQuestionLog(prevQuestion);
      sessionStorage.setItem("previousQuestionSubmitted", "");
    }
  });

  // Keep the existing event listener useEffect
  useEffect(() => {
    window.addEventListener(
      "virtualLibrarianClearChatUpdated",
      handleClearChat
    );
    window.addEventListener(
      "virtualLibrarianQuestionSubmittedUpdated",
      handleSubmitQuestion
    );

    return () => {
      window.removeEventListener(
        "virtualLibrarianClearChatUpdated",
        handleClearChat
      );
      window.removeEventListener(
        "virtualLibrarianQuestionSubmittedUpdated",
        handleSubmitQuestion
      );
    };
  }, []);

  const tabStyle = {
    fontWeight: 600,
    fontSize: "16px",
    textTransform: "none",
    color: "#ADADAD",
    "&.Mui-selected": {
      color: "#525252",
      borderBottom: 2.5,
      borderColor: "divider",
    },
  };

  return (
    <div className="vl-content-wrapper">
      {mode !== "user" && <h3>Chat Playground</h3>}
      <Box
        sx={{ borderBottom: 1, borderColor: "divider" }}
        className="chat-panel-header"
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label="basic tabs example"
          textColor="inherit"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "white",
              height: "4px",
            },
          }}
        >
          <Tab label="Enhanced Search Results" sx={tabStyle} />
          {videoCitations?.length > 0 && (
            <Tab
              label={`Videos (${
                new Set(videoCitations.map((citation) => citation.content_id))
                  .size
              })`}
              sx={tabStyle}
            />
          )}
          {documentCitations?.length > 0 && (
            <Tab
              label={`Documents (${
                new Set(
                  documentCitations.map((citation) => citation.content_id)
                ).size
              })`}
              sx={tabStyle}
            />
          )}
        </Tabs>
      </Box>
      <TabPanel value={selectedTab} index={0} className="chat-panel">
        <div className="chat-panel-content">
          <div className="chat-panel-content-inner">
            {chatItems.map((item, index) => (
              <div key={index}>
                <div className="chat-panel-content-inner-question background-set">
                  <img
                    src={echoImage}
                    alt="Thinking..."
                    style={{ marginRight: "8px", height: "36px" }}
                  />
                  <p>{item.question}</p>
                </div>
                <div
                  className="answerText"
                  dangerouslySetInnerHTML={{ __html: item.answerHtml }}
                  onClick={handleCitationClick}
                ></div>
              </div>
            ))}
            {isThinking && (
              <div>
                <div className="chat-panel-content-inner-question">
                  <img
                    src={spinnerIcon}
                    alt="Thinking..."
                    className="thinking-spinner"
                    style={{ marginRight: "8px", height: "36px" }}
                  />
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#333",
                    }}
                  >
                    Thinking...
                  </p>
                </div>
              </div>
            )}
            <div ref={chatItemsEndRef} />
          </div>
          <div className="previous-history-container">
            <div
              style={{
                overflowY: "auto",
                paddingRight: "10px",
              }}
            >
              <h4
                style={{
                  marginTop: 0,
                  marginBottom: "10px",
                  color: "#666",
                  fontWeight: 500,
                  fontSize: "16px",
                }}
              >
                Previous Questions
              </h4>
              <hr
                style={{
                  border: 0,
                  height: "1px",
                  backgroundColor: "#ddd",
                  margin: "0 0 15px 0",
                }}
              />

              {suggestedQuestions.length > 0 ? (
                suggestedQuestions.map((suggestedQuestion, index) => {
                  // Format the date
                  const date = new Date(suggestedQuestion?.timestamp);
                  const formattedDate = `${date.toLocaleDateString("en-US", {
                    month: "short",
                  })} ${date.getDate()} ${date.toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}`;

                  return (
                    <div key={index} style={{ marginBottom: "20px" }}>
                      <p
                        style={{
                          color: "#777",
                          fontSize: "11px",
                          margin: "0 0 3px 0",
                        }}
                      >
                        {formattedDate}
                      </p>
                      <p
                        style={{
                          cursor: "pointer",
                          color: "#00527B",
                          margin: 0,
                          fontSize: "13px",
                          fontWeight: 500,
                          lineHeight: "18px",
                        }}
                        onClick={() =>
                          showPreviousQuestionLog(suggestedQuestion?.text)
                        }
                      >
                        {suggestedQuestion?.text}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "5px",
                          gap: "10px",
                        }}
                      >
                        {suggestedQuestion?.documentCount > 0 && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "3px",
                              fontSize: "11px",
                              color: "#777",
                            }}
                          >
                            <VideosIcon className="svg-icon" />
                            <span style={{ fontSize: "11px" }}>
                              {suggestedQuestion?.videoCount}
                            </span>
                          </div>
                        )}
                        {suggestedQuestion?.documentCount > 0 && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "3px",
                              fontSize: "11px",
                              color: "#777",
                            }}
                          >
                            <FileIcon className="svg-icon" />
                            <span style={{ fontSize: "11px" }}>
                              {suggestedQuestion?.documentCount}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p>Loading previous questions...</p>
              )}
            </div>
          </div>
        </div>
      </TabPanel>
      {videoCitations?.length > 0 && (
        <TabPanel value={selectedTab} index={1} className="chat-panel">
          <div className="chat-panel-content-inner">
            <VirtualLibrarianResultContentList citations={videoCitations} />
          </div>
        </TabPanel>
      )}
      {documentCitations?.length > 0 && (
        <TabPanel
          value={selectedTab}
          index={videoCitations?.length > 0 ? 2 : 1}
          className="chat-panel"
        >
          <div className="chat-panel-content-inner">
            <VirtualLibrarianResultContentList citations={documentCitations} />
          </div>
        </TabPanel>
      )}
    </div>
  );
};

export default VirtualLibrarianChat;
